/* eslint-disable nimbus/prefer-until-destroyed */
import { Inject, Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  PopupEvent,
  PopupRequest,
  SilentRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import { Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { of, BehaviorSubject, throwError, Observable } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { catchError, filter, map, mergeMap, tap, delay, concatMap, switchMap, finalize, take, timeout } from 'rxjs/operators';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { Profile } from '../models/profile';
import { AuthRequest, IAuthRequest, msalAuthBaseUrl } from '../auth-config';
import { AssistantEnvironmentConfiguration } from '../interfaces/assistant-environment.configuration';
import { AppRoutes } from '../enums/app-routes';
import { MessageService } from './message.service';
import { GraphApiService } from './graph-api.service';
import { ImageService } from './image.service';
import { ApiRoutes } from './api/api-routes.consts';
import { LoggingService } from './logging.service';
import { ErrorService } from './error.service';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly LOGIN_HINT_KEY = 'Nimbus.Assistant.LoginHint';
  // explicitlyLoggedOut used to live only in memory. On an app reload/restart after an explicit sign-out
  // (SPA reload, not just a fresh install) the field reset to false, so tryLogInSilently attempted
  // ssoSilent again with no hint and no account — guaranteed to fail (AADSTS50058) — and repeated that
  // forever on every keep-alive tick. Persist it so a cold start remembers an explicit sign-out.
  private static readonly EXPLICIT_LOGOUT_KEY = 'Nimbus.Assistant.ExplicitlyLoggedOut';

  // MSAL writes a small set of well-known key shapes to web storage. Pattern-based (rather than a
  // "not Nimbus-prefixed" deny-list) because NA's own keys in local-storage.service.ts don't carry
  // a Nimbus prefix — prefix-negation would nuke app state. Patterns target documented MSAL shapes.
  private static readonly MSAL_KEY_PATTERNS: ReadonlyArray<RegExp> = [
    /^msal\./i,
    /-login\.windows\.net-/i,
    /-login\.microsoftonline\.com-/i,
    /\.accesstoken\./i,
    /\.idtoken\./i,
    /\.refreshtoken\./i,
    /\.account\./i,
    /^[0-9a-f-]{36}\./i,
  ];

  private get teamsTimeoutSec(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.configurationService.configurationSettings$.value?.authTeamsTokenTimeoutSec ?? 60;
  }

  private explicitlyLoggedOut: boolean = false;
  private keepAlivePollingInterval;
  private keepAlivePollingIntervalMs = 20_000;
  private tokenFefreshBufferMs = 600_000;
  private tokenForceFefreshBufferMs = 10_000;
  private closeMsalPopupsTimeoutMs = 5_000;
  // ssoSilent's hidden-iframe flow can hang indefinitely in WebView2 (SPA app-type + iframe sandbox
  // combination — the /token redemption never resolves). Cap it so a hang becomes a TimeoutError
  // the catch block can recover from via the loginPopup fallback.
  private ssoSilentTimeoutMs = 8_000;
  // Bounds the tryLogInSilently loginPopup fallback so a COOP-blocked popup (#149223) can't hang
  // isTryingSilentLogin true forever and silently strand the user signed out.
  private loginPopupFallbackTimeoutMs = 15_000;

  private readonly aadSpaTokenCorsErrorNo = 9002327;
  // MSAL BrowserAuthError codes that mean the request never reached AAD (network/CORS/WebView blip).
  // Treating these as a logout signal kills autologin — see #135988.
  private readonly transientAuthErrorCodes: ReadonlyArray<string> = ['post_request_failed', 'no_network_connectivity', 'network_error'];

  private idToken: string;
  private idTokenExp: number = Date.now();

  private forceRefreshRequest = { ...(this.msalGuardConfig.authRequest as IAuthRequest), forceRefresh: true } as SilentRequest;
  private loggedOutByExpiredToken: boolean;
  // processLogout can be reached twice for a single sign-out (LOGOUT_SUCCESS plus the logout
  // finalize fallback). It sends a LogOut message to the desktop host, so make it run once per
  // session teardown instead of duplicating host messages and navigations.
  private logoutProcessed: boolean = false;
  private isTryingSilentLogin: boolean = false;
  private msalPopups = [];

  public authenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public accessTokenReceived$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public teamsTokenReceived$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public tokenAquiredByPopup$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public interactionStatus$ = new BehaviorSubject<InteractionStatus>(InteractionStatus.Startup);

  public profile$: BehaviorSubject<Profile> = new BehaviorSubject<Profile>({});
  public avatar$: BehaviorSubject<SafeUrl> = new BehaviorSubject<SafeUrl>(null);

  public constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly http: BaseHttpService,
    private readonly graphApiSerice: GraphApiService,
    private imageService: ImageService,
    private environmentService: EnvironmentConfigurationService,
    private readonly loggingService: LoggingService,
    private readonly errorService: ErrorService,
    protected readonly configurationService: ConfigurationService,
  ) {
    // Migration sweep: pre-MemoryStorage installs left MSAL tokens in the WebView2 LevelDB on disk.
    // Clear them once at startup so dormant users (who never explicitly log out) stop carrying them
    // forward. Idempotent after the first run on a clean install.
    this.sweepLegacyMsalKeys(localStorage);
    this.sweepLegacyMsalKeys(sessionStorage);
    this.explicitlyLoggedOut = localStorage.getItem(AuthService.EXPLICIT_LOGOUT_KEY) === 'true';
    this.startKeepAlivePolling();
    this.initMsalSubscriptions();
  }

  public login(): void {
    this.loggingService.Info(`Invoke login`);
    this.setExplicitlyLoggedOut(false);
    MessageService.SendTopmostMessage(false);
    this.msalService
      .loginPopup({ ...AuthRequest.loginPopup } as PopupRequest)
      .pipe(
        catchError((err) => {
          this.processLogout();
          this.clearActiveAccount();
          this.errorService.showLoginError(err);
          return of(null);
        }),
        finalize(() => MessageService.SendTopmostMessage(null)),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  public acquireTeamsToken(): Observable<string> {
    if (!this.authenticated$.value) {
      return of(null);
    }

    return this.acquireToken(AuthRequest.teams).pipe(
      tap((result) => {
        if (!result || !result.accessToken) {
          throwError('Access token aquire error');
        }
      }),
      mergeMap((result: AuthenticationResult) =>
        this.http.post(ApiRoutes.TeamsToken.getTeamsToken(), { token: result.accessToken }).pipe(map((t) => (t ? t.token : null))),
      ),
      tap((token) => {
        if (!token) {
          throwError('Teams token aquire error');
        } else {
          this.teamsTokenReceived$.next(true);
        }
      }),
      catchError((err) => {
        this.loggingService.Error(`getTeamsToken error: ${JSON.stringify(err)}`);

        this.teamsTokenReceived$.next(false);

        if (!this.authenticated$.value) {
          return of(null);
        }

        return of(null).pipe(
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          delay(this.teamsTimeoutSec * 1000),
          concatMap((_) => this.acquireTeamsToken()),
        );
      }),
    );
  }

  public aquireApiToken(scopes: string[]): Observable<AuthenticationResult> {
    const account = this.msalService.instance.getActiveAccount();

    const config = {
      scopes,
      authority: `${msalAuthBaseUrl}/${account?.tenantId}`,
    };
    return this.acquireToken(config as IAuthRequest, false).pipe(
      tap((x) => {
        if (!x) {
          throwError('Empty token');
        }
      }),
      catchError((err) => {
        this.loggingService.Error(`aquireApiToken error: ${JSON.stringify(err)}`);
        return of(null);
      }),
    );
  }

  public getTenantId(): string {
    const account = this.msalService.instance.getActiveAccount();
    return account?.tenantId;
  }

  public acquireIdToken(): Observable<string> {
    if (Date.now() < this.idTokenExp - this.tokenFefreshBufferMs) {
      return of(this.idToken);
    }

    if (Date.now() < this.idTokenExp - this.tokenForceFefreshBufferMs) {
      this.refreshIdToken(this.forceRefreshRequest).subscribe();
      return of(this.idToken);
    }

    return this.refreshIdToken(this.forceRefreshRequest);
  }

  public logout(): void {
    this.loggingService.Info(`Invoke logout`);
    this.authenticated$.next(false);
    this.setExplicitlyLoggedOut(true);
    this.clearLoginHint();
    MessageService.SendTopmostMessage(false);
    this.msalService
      .logout()
      .pipe(
        finalize(() => {
          this.clearAllAuthState(true);
          this.processLogout();
          MessageService.SendTopmostMessage(null);
        }),
      )
      .subscribe();
  }

  public logoutPopup(autoClosePopup: boolean = false, explicitlyLoggedOut: boolean = true): Observable<void> {
    this.setExplicitlyLoggedOut(explicitlyLoggedOut);
    this.authenticated$.next(false);
    this.loggingService.Info(`Invoke logoutPopup`);
    if (autoClosePopup) {
      setTimeout(() => {
        this.closeMsalPopups();
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      }, 5000);
    }
    // Drop the hint up front, not in finalize: MSAL resolves logoutPopup by polling
    // popupWindow.closed, which COOP blocks in the WebView ("Cross-Origin-Opener-Policy policy would
    // block the window.closed call"). The observable can then never complete, so a finalize-only
    // clear leaves the hint behind and the next start pre-populates the UPN of the account the user
    // just signed out of.
    if (explicitlyLoggedOut) {
      this.clearLoginHint();
    }
    MessageService.SendTopmostMessage(false);
    return this.msalService.logoutPopup().pipe(
      finalize(() => {
        // Keep the persisted loginHint on involuntary logout (token blip) so the next launch can
        // still autologin; drop it only when the user explicitly signed out.
        this.clearAllAuthState(explicitlyLoggedOut);
        // MSAL only emits LOGOUT_SUCCESS when the popup completes its round trip; if it is dismissed
        // or the WebView swallows the event, processLogout would never run and the app would stay on
        // the signed-in screen until a reload (#154262). Running it here is idempotent.
        this.processLogout();
        MessageService.SendTopmostMessage(null);
      }),
    );
  }

  public getPermissionLink(): string {
    const tenantId = this.getTenantId();
    const appId = (this.environmentService.configurationSettings as AssistantEnvironmentConfiguration)?.acsAdAppId;
    const redirectUri = `https://portal.${this.environmentService.configurationSettings.mainLocation}/html/authentication/end.html`;
    return tenantId && appId
      ? `https://login.microsoftonline.com/${tenantId}/v2.0/adminconsent?client_id=${appId}&redirect_uri=${redirectUri}&scope=openid+profile+email+offline_access+User.Read+User.Read.All+Presence.Read+https://auth.msft.communication.azure.com/Teams.ManageCalls+https://auth.msft.communication.azure.com/Teams.ManageChats`
      : '';
  }

  private refreshIdToken(authRequest: IAuthRequest = null): Observable<string> {
    return this.acquireToken(authRequest).pipe(
      tap((authResult) => {
        if (!authResult) {
          throwError(`auth result is null`);
        }
      }),
      catchError((err) => {
        this.loggingService.Error(`refreshIdToken acquireToken error: ${JSON.stringify(err)}`);
        this.idTokenExp = Date.now();
        this.idToken = null;
        return of(null);
      }),
      map((token) => {
        if (token) {
          this.idToken = token.idToken;
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          this.idTokenExp = token.idTokenClaims ? token.idTokenClaims['exp'] * 1000 : Date.now();
        }
        return token?.idToken;
      }),
    );
  }

  private acquireToken(
    authRequest: IAuthRequest = this.msalGuardConfig.authRequest as IAuthRequest,
    logoutOnFail: boolean = true,
  ): Observable<AuthenticationResult> {
    if (!this.authenticated$.value) {
      if (this.loggedOutByExpiredToken) {
        this.loggingService.Info(`loggedOutByExpiredToken tryLogingSilently`);
        this.tryLogInSilently();
      }
      return of(null);
    }

    return this.msalService.acquireTokenSilent(authRequest).pipe(
      tap((x: AuthenticationResult) => {
        if (x.expiresOn < new Date()) {
          throwError(`Token expired`);
        }
      }),
      catchError((err) => {
        this.loggingService.Error(`acquireTokenSilent error: ${JSON.stringify(err)}`);

        // AADSTS9002327: SPA token CORS restriction in WebView (protocol error, not a credential
        // failure). Triggering logout here causes an infinite restart loop — handled in tryLogInSilently.
        // Transient MSAL BrowserAuthError network failures (post_request_failed, etc.) must also skip
        // logout so the user stays signed in across brief network blips (e.g. WebView reload, unlock).
        if (
          err?.errorNo === this.aadSpaTokenCorsErrorNo ||
          err?.errorCode === 'invalid_request' ||
          this.transientAuthErrorCodes.includes(err?.errorCode)
        ) {
          return of(null);
        }

        const noInteraction = this.interactionStatus$.value === InteractionStatus.None;
        if (noInteraction && !logoutOnFail) {
          return this.acquireTokenPopup(authRequest, logoutOnFail);
        }

        if (logoutOnFail) {
          this.processLogout(); // sets authenticated$ to false and stops pollings
          if (noInteraction) {
            MessageService.SendBringToFrontMessage();
            this.logoutPopup(true, false).subscribe();
            this.loggedOutByExpiredToken = true;
          }
        }
        return of(null);
      }),
    );
  }

  private acquireTokenPopup(
    authRequest: IAuthRequest = this.msalGuardConfig.authRequest as IAuthRequest,
    logoutOnFail: boolean = true,
  ): Observable<AuthenticationResult> {
    this.loggingService.Info(`Invoke acquireTokenPopup`);
    return this.msalService.acquireTokenPopup(authRequest).pipe(
      catchError((err) => {
        this.loggingService.Error(`acquireTokenPopup error: ${JSON.stringify(err)}`);
        if (this.authenticated$.value && logoutOnFail) {
          MessageService.SendBringToFrontMessage();
          this.logoutPopup(true, false).subscribe();
        }
        return of(null);
      }),
      tap((authResult: AuthenticationResult) => {
        if (!!authResult?.accessToken) {
          this.tokenAquiredByPopup$.next(true);
        }
      }),
    );
  }

  private startKeepAlivePolling(): void {
    if (this.keepAlivePollingInterval) {
      clearInterval(this.keepAlivePollingInterval);
    }

    const keepAliveCall = (): void => {
      // do not keep polling if explicitly logged out
      if (this.explicitlyLoggedOut) {
        return;
      }

      // when session expired, retry silent login periodically instead of acquiring tokens
      if (this.loggedOutByExpiredToken) {
        this.tryLogInSilently();
        return;
      }

      this.acquireIdToken()
        .pipe(take(1))
        .subscribe(() => {
          if (Date.now() < this.idTokenExp) {
            this.accessTokenReceived$.next(true);
          }
        });
    };

    this.keepAlivePollingInterval = setInterval(keepAliveCall.bind(this), this.keepAlivePollingIntervalMs);
  }

  private async initMsalSubscriptions(): Promise<void> {
    this.msalBroadcastService.inProgress$
      .pipe(
        tap((status: InteractionStatus) => {
          this.interactionStatus$.next(status);
        }),
        filter((status: InteractionStatus) => status === InteractionStatus.None),
      )
      .subscribe(this.checkAndSetActiveAccount.bind(this));

    this.msalBroadcastService.msalSubject$.subscribe(async (msg: EventMessage) => {
      switch (msg && msg.eventType) {
        case EventType.LOGIN_SUCCESS: {
          const authResult = msg && (msg.payload as AuthenticationResult);
          if (authResult) {
            this.msalService.instance.setActiveAccount(authResult.account);
          }
          this.processLogin();
          break;
        }
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          this.accessTokenReceived$.next(true);
          if (!this.authenticated$.value && !this.explicitlyLoggedOut) {
            // A token was just acquired (typically ssoSilent populating MSAL's in-memory cache after a
            // reload) but we are not flagged authenticated yet. The ssoSilent observable can stay hung
            // in WebView2 even after this event fires (the /token redemption promise never resolves —
            // see tryLogInSilently), so don't wait on it or start another attempt: promote the freshly
            // cached account to active and finish the login now (#135988).
            this.checkAndSetActiveAccount();
          }
          break;
        }
        case EventType.ACQUIRE_TOKEN_FAILURE: {
          this.accessTokenReceived$.next(false);
          break;
        }
        case EventType.LOGOUT_SUCCESS: {
          this.processLogout();
          break;
        }
        default:
          break;
      }
    });

    await this.msalService.instance.initialize();

    this.msalService.instance.addEventCallback((message) => {
      if (message.eventType === EventType.POPUP_OPENED) {
        this.msalPopups.push((message.payload as PopupEvent).popupWindow);
      }
    });

    this.tryLogInSilently();
  }

  private tryLogInSilently(): void {
    // Autologin is only allowed on startup (app launch / OS session start). Once the user signed out
    // explicitly, stay signed out until they log in again (#154262).
    if (this.explicitlyLoggedOut) {
      this.loggingService.Info('tryLogInSilently: skipped, user logged out explicitly');
      return;
    }

    if (this.isTryingSilentLogin) {
      this.loggingService.Info('tryLogInSilently: already in progress, skipping');
      return;
    }
    this.isTryingSilentLogin = true;

    const activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();
    const authRequest = cloneDeep(this.forceRefreshRequest);
    authRequest.account = activeAccount ?? (accounts?.length ? accounts[0] : null);

    // MemoryStorage means MSAL's account cache is empty on every cold start, so build an ssoSilent
    // request augmented with a persisted UPN loginHint. AAD's session cookie in the WebView2 cookie
    // jar does the rest. loginHint lives on SsoSilentRequest, not SilentRequest, so we keep it on
    // a separate object — acquireTokenSilent then picks up the active account ssoSilent populated.
    const ssoSilentRequest: SsoSilentRequest = { ...authRequest };
    if (!authRequest.account) {
      const persistedHint = localStorage.getItem(AuthService.LOGIN_HINT_KEY);
      if (persistedHint) {
        ssoSilentRequest.loginHint = persistedHint;
      }
    }

    // We recommended to using the login_hint optional ID token claim provided to ssoSilent as loginHint
    // as it is the most reliable account hint of silent and interactive requests.
    // https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-sso

    // Passing an account will look for the login_hint optional ID token claim (preferred),
    // then the sid optional id token claim, then fall back to loginHint (if provided) or account username.
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#silent-login-with-ssosilent

    this.loggingService.Info(
      `Silent login attempt. Account: ${authRequest.account?.name} (active: ${!!activeAccount}, hint: ${ssoSilentRequest.loginHint})`,
    );

    this.msalService
      .ssoSilent(ssoSilentRequest)
      .pipe(
        switchMap(() => {
          // ssoSilent populated MSAL's account cache (it bootstrapped from loginHint with no
          // account on the request). Re-read it so acquireTokenSilent doesn't fail with
          // no_account_error and we autologin in one tick instead of waiting for keep-alive.
          const refreshedAccount =
            this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()?.[0] ?? authRequest.account;
          if (!refreshedAccount) {
            return of(null);
          }
          return this.msalService.acquireTokenSilent({ ...authRequest, account: refreshedAccount });
        }),
        // Guard the whole silent chain, not just ssoSilent: acquireTokenSilent can also hang on the
        // same WebView2 /token redemption issue, and a hang past this point would otherwise leave
        // isTryingSilentLogin stuck true forever, blocking every later autologin attempt (#135988).
        timeout(this.ssoSilentTimeoutMs),
        tap(() => {
          this.loggedOutByExpiredToken = false;
        }),
        catchError((err) => {
          this.loggingService.Info(`ssoSilent/aquireTokenSilent attempt was not successful: ${JSON.stringify(err)}`);

          // ssoSilent can redeem the code and write a valid token to MSAL's in-memory cache (firing
          // ACQUIRE_TOKEN_SUCCESS) while its own observable never resolves in WebView2 — our timeout
          // then surfaces here even though the user is effectively signed in. When we started without
          // an account (cold start / reload under MemoryStorage), any account now in the cache was put
          // there by this ssoSilent's successful code redemption, so finish the login instead of
          // flipping to logged-out and opening a popup the WebView blocks for lack of a user gesture.
          // Gated on the empty-start case so a genuine failure with a pre-existing (stale) account
          // still falls through to the popup path below (#135988).
          if (!authRequest.account) {
            const cachedAccount = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()?.[0];
            if (cachedAccount) {
              this.loggingService.Info('Silent login: account already cached after ssoSilent, completing login without popup');
              this.msalService.instance.setActiveAccount(cachedAccount);
              this.loggedOutByExpiredToken = false;
              return of(null);
            }
          }

          // Transient MSAL BrowserAuthError (network/CORS/WebView blip) — leave session state untouched
          // and let the next keep-alive tick retry. Flipping loggedOutByExpiredToken here strands the
          // user on the login screen when the MSAL account cache is empty (see #135988).
          if (this.transientAuthErrorCodes.includes(err?.errorCode)) {
            return of(null);
          }
          this.emitOnNotAuthenticated();
          this.loggedOutByExpiredToken = true;
          const isProtocolError = err?.errorNo === this.aadSpaTokenCorsErrorNo || err?.errorCode === 'invalid_request';
          // Fall back to loginPopup when we can identify the user — either via cached account
          // (original path) or via a persisted UPN hint (cold-start under MemoryStorage). Popup
          // sidesteps the WebView2 iframe-sandbox + SPA-CORS issues that hang or fail ssoSilent;
          // AAD's session cookie does silent SSO inside the popup, so the user sees only a brief
          // flash, not a credential prompt.
          const popupHint = ssoSilentRequest.loginHint ?? authRequest.account?.username;
          const shouldPopup = popupHint || (authRequest.account && !isProtocolError);
          if (shouldPopup) {
            const popupRequest: PopupRequest = { ...AuthRequest.loginPopup } as PopupRequest;
            if (popupHint) {
              popupRequest.loginHint = popupHint;
            }
            // Bring the app to front before opening the popup: unlike the expired-token path in
            // acquireToken, this fallback previously opened silently, so a popup that did need
            // interaction (or just needed focus to run its silent SSO check) could sit unnoticed
            // behind other windows.
            MessageService.SendBringToFrontMessage();
            return this.msalService.loginPopup(popupRequest).pipe(
              // Same COOP issue as logoutPopup (#149223): the WebView can block MSAL's
              // popupWindow.closed poll, so this observable can hang forever. Without a bound here,
              // isTryingSilentLogin (cleared only in the outer finalize below) never resets, and every
              // later keep-alive tick just logs "already in progress, skipping" — silently stranding
              // the user signed out for hours until the hang resolves on its own or they restart.
              timeout(this.loginPopupFallbackTimeoutMs),
              catchError((popupErr) => {
                this.loggingService.Info(`Fallback loginPopup did not complete: ${JSON.stringify(popupErr)}`);
                this.closeMsalPopups();
                return of(null);
              }),
              finalize(() => {
                setTimeout(() => {
                  this.closeMsalPopups();
                }, this.closeMsalPopupsTimeoutMs);
              }),
            );
          }
          return of(null);
        }),
        finalize(() => {
          this.isTryingSilentLogin = false;
        }),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  private checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();

    // Promote a signed-in account to active first, then log in. ssoSilent leaves the account in the
    // cache (getAllAccounts) without marking it active, so reading activeAccount before setting it
    // would skip processLogin and strand cold-start/reload autologin (#135988).
    if (!activeAccount && accounts?.length) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    // Never log back in on top of an explicit user logout: MSAL can still report a cached account
    // (e.g. logoutPopup dismissed, or a second signed-in account) and inProgress$ fires None right
    // after the logout popup closes, which would silently re-authenticate the user (#154262).
    if (this.explicitlyLoggedOut) {
      return;
    }

    if (activeAccount && !this.loggedOutByExpiredToken) {
      this.processLogin();
    }
  }

  private closeMsalPopups(): void {
    try {
      this.msalPopups.forEach((popupWindow) => {
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
      });
    } catch (err) {
      this.loggingService.Error(`Closing popups error: ${JSON.stringify(err)}`);
    }
    this.msalPopups = [];
  }

  private processLogin(): void {
    this.loggedOutByExpiredToken = false;
    this.logoutProcessed = false;
    this.closeMsalPopups();

    if (this.authenticated$.value) {
      return;
    }
    this.loggingService.Info('AUTHENTICATED: true');
    this.authenticated$.next(true);
    this.setExplicitlyLoggedOut(false);

    // Persist the UPN (a non-secret identifier, not a token) so cross-restart ssoSilent has a
    // loginHint to send when MSAL's MemoryStorage account cache is empty on the next launch.
    const account = this.msalService.instance.getActiveAccount();
    if (account?.username) {
      localStorage.setItem(AuthService.LOGIN_HINT_KEY, account.username);
    }

    this.graphApiSerice
      .getMe()
      .pipe(
        catchError((err) => {
          this.loggingService.Error(`graphApiSerice getMe error: ${JSON.stringify(err)}`);
          return of(null);
        }),
      )
      .subscribe((profile) => this.profile$.next(profile || {}));

    this.graphApiSerice
      .getMyProfileIcon()
      .pipe(
        catchError((err) => {
          this.loggingService.Error(`graphApiSerice getMyProfileIcon error: ${JSON.stringify(err)}`);
          return of(null);
        }),
      )
      .subscribe((image) => {
        const imageUrl = this.imageService.getImageBlobUrl(image);
        this.avatar$.next(imageUrl);
      });

    this.router.navigate([AppRoutes.home]);

    MessageService.SendLogin();
  }

  private processLogout(): void {
    if (this.logoutProcessed) {
      return;
    }
    this.logoutProcessed = true;
    this.idTokenExp = Date.now();
    this.idToken = null;
    this.emitOnNotAuthenticated();
    MessageService.SendLogout();
    // Navigate to the login route explicitly. Navigating to '' redirects to AppRoutes.home, which is
    // protected by MsalGuard: with no account left in the cache the guard calls loginPopup and keeps
    // the navigation pending, so the UI stayed on the signed-in screen and an unwanted login popup
    // appeared right after the user signed out (#154262).
    this.router.navigate([AppRoutes.login]);
  }

  private emitOnNotAuthenticated(): void {
    this.profile$.next({});
    this.avatar$.next(undefined);
    this.authenticated$.next(false);
    this.accessTokenReceived$.next(false);
  }

  private clearActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      this.msalService.instance.setActiveAccount(null);
      this.loggingService.Info(`Account ${activeAccount.username} was set off from active state.`);
    }
  }

  private clearLoginHint(): void {
    localStorage.removeItem(AuthService.LOGIN_HINT_KEY);
  }

  private setExplicitlyLoggedOut(value: boolean): void {
    this.explicitlyLoggedOut = value;
    if (value) {
      localStorage.setItem(AuthService.EXPLICIT_LOGOUT_KEY, 'true');
    } else {
      localStorage.removeItem(AuthService.EXPLICIT_LOGOUT_KEY);
    }
  }

  private clearAllAuthState(includeLoginHint: boolean): void {
    // Should be a no-op under MemoryStorage but covers any edge case where MSAL writes elsewhere.
    this.msalService.instance.clearCache().catch((err) => {
      this.loggingService.Error(`clearCache error: ${JSON.stringify(err)}`);
    });
    this.sweepLegacyMsalKeys(localStorage);
    this.sweepLegacyMsalKeys(sessionStorage);
    if (includeLoginHint) {
      this.clearLoginHint();
    }
  }

  private sweepLegacyMsalKeys(store: Storage): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && AuthService.MSAL_KEY_PATTERNS.some((re) => re.test(key))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => store.removeItem(k));
  }
}
